import { supabase } from './supabase';

interface Campaign {
  id: string;
  status: 'active' | 'completed' | 'draft';
  name: string;
  user_id: string;
  max_tickets?: number;
}

interface Entry {
  id: string;
  campaign_id: string;
  email: string;
  name: string;
  ticket_number: number;
  payment_status: string;
  payment_method: string;
  total_amount: number;
  created_at: string;
  is_winner: boolean;
  prize_status?: string;
  prize_value?: number;
  prize?: boolean;
}

interface AnalyticsData {
  entryStats: {
    totalEntries: number;
    paidEntries: number;
    pendingEntries: number;
    failedEntries: number;
    entriesByDate: Array<{ date: string; count: number }>;
    averageEntriesPerDay: number;
  };
  paymentAnalytics: {
    totalRevenue: number;
    onlinePayments: number;
    inPersonPayments: number;
    paymentsByDate: Array<{ date: string; amount: number }>;
    revenueByMethod: Record<string, number>;
    paymentStatusDistribution: Record<string, number>;
  };
  campaignPerformance: Array<any>;
  prizeDistribution: {
    totalPrizes: number;
    prizesByStatus: Record<string, number>;
    averagePrizeValue: number;
  };
  participantStats: {
    totalTicketsSold: number;
    uniqueEmails: number;
    entriesPerParticipant: number;
  };
}

export async function fetchAnalyticsData(userId: string): Promise<AnalyticsData> {
  try {
    // First fetch organizations owned by this user
    const { data: organizations, error: orgsError } = await supabase
      .from('organizations')
      .select('id')
      .eq('owner_id', userId);

    if (orgsError) throw orgsError;

    // Then fetch campaigns for these organizations
    const orgIds = organizations.map(org => org.id);
    const { data: campaigns, error: campaignsError } = await supabase
      .from('campaigns')
      .select('*')
      .in('org_id', orgIds);

    if (campaignsError) throw campaignsError;

    // Then fetch entries for these campaigns
    const campaignIds = campaigns.map(c => c.id);
    const { data: entries, error: entriesError } = await supabase
      .from('entries')
      .select('*, campaign:campaign_id(*)')
      .in('campaign_id', campaignIds);

    if (entriesError) throw entriesError;

    const paidEntries = entries.filter(e => e.payment_status === 'paid');
    const pendingEntries = entries.filter(e => e.payment_status === 'pending');
    const failedEntries = entries.filter(e => e.payment_status === 'failed');
    const onlinePayments = entries.filter(e => e.payment_method === 'online').length;
    const inPersonPayments = entries.filter(e => e.payment_method === 'inPerson').length;

    // Process the data
    return {
      entryStats: {
        totalEntries: entries.length,
        paidEntries: paidEntries.length,
        pendingEntries: pendingEntries.length,
        failedEntries: failedEntries.length,
        entriesByDate: processEntriesByDate(entries),
        averageEntriesPerDay: entries.length / (Object.keys(processEntriesByDate(entries)).length || 1),
      },
      paymentAnalytics: {
        totalRevenue: paidEntries.reduce((sum, entry) => sum + (entry.total_amount || 0), 0),
        onlinePayments,
        inPersonPayments,
        paymentsByDate: calculatePaymentsByDate(entries),
        revenueByMethod: {
          online: paidEntries.filter(e => e.payment_method === 'online')
            .reduce((sum, entry) => sum + (entry.total_amount || 0), 0),
          inPerson: paidEntries.filter(e => e.payment_method === 'inPerson')
            .reduce((sum, entry) => sum + (entry.total_amount || 0), 0),
        },
        paymentStatusDistribution: {
          paid: paidEntries.length,
          pending: pendingEntries.length,
          failed: failedEntries.length,
        },
      },
      campaignPerformance: processCampaignPerformance(campaigns, entries),
      prizeDistribution: {
        totalPrizes: entries.filter(e => e.is_winner).length,
        prizesByStatus: {
          awarded: entries.filter(e => e.is_winner && e.prize).length,
          pending: entries.filter(e => e.is_winner && !e.prize).length,
        },
        averagePrizeValue: entries.filter(e => e.prize)
          .reduce((sum, entry) => sum + (entry.prize?.value || 0), 0) / 
          entries.filter(e => e.prize).length || 0,
      },
      participantStats: {
        totalTicketsSold: entries.length,
        uniqueEmails: new Set(entries.map(e => e.email)).size,
        entriesPerParticipant: entries.length / (new Set(entries.map(e => e.email)).size || 1),
      },
    };
  } catch (error) {
    console.error('Error fetching analytics data:', error);
    throw error;
  }
}

// Helper functions
function processEntriesByDate(entries: Entry[]): Array<{ date: string; count: number }> {
  const entriesByDate = entries.reduce((acc: Record<string, number>, entry) => {
    const date = new Date(entry.created_at).toLocaleDateString();
    acc[date] = (acc[date] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(entriesByDate).map(([date, count]) => ({
    date,
    count
  }));
}

function calculatePaymentsByDate(entries: Entry[]): Array<{ date: string; amount: number }> {
  const paymentsByDate = entries.reduce((acc: Record<string, number>, entry) => {
    if (entry.payment_status === 'paid') {
      const date = new Date(entry.created_at).toLocaleDateString();
      acc[date] = (acc[date] || 0) + (entry.total_amount || 0);
    }
    return acc;
  }, {});

  return Object.entries(paymentsByDate).map(([date, amount]) => ({
    date,
    amount
  }));
}

function processCampaignPerformance(campaigns: Campaign[], entries: Entry[]): Array<any> {
  return campaigns.map(campaign => {
    const campaignEntries = entries.filter(entry => entry.campaign_id === campaign.id);
    const totalRevenue = campaignEntries.reduce((sum, entry) => 
      sum + (entry.total_amount || 0), 0
    );
    const conversionRate = campaign.max_tickets ? 
      (campaignEntries.length / campaign.max_tickets) * 100 : 0;

    return {
      name: campaign.name,
      totalEntries: campaignEntries.length,
      totalRevenue,
      conversionRate
    };
  });
} 